import { Box, Card, Chip, Grid, Typography, Paper } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { getCustomerOrders } from './customerOrdersSlice';
import { useTranslation } from 'react-i18next';
import OrderStatusLabel from '../../../app/components/OrderStatusLabel';
import { formatCurrency } from '../../../app/utils/formatters';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getOrderTypeChipProps } from '../../../app/utils/orderUtils';
import ListTitleBox from '../../../app/components/list/ListTitleBox';

export default function CustomerOrderList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { orders } = useAppSelector((state) => state.customerOrders);
  const { cart } = useAppSelector((state) => state.cart);

  useEffect(() => {
    dispatch(getCustomerOrders());
  }, [dispatch]);

  const handleBackClick = () => {
    if (cart.restaurantId) {
      navigate(`/customer/restaurants/${cart.restaurantId}`);
    } else {
      navigate(-1);
    }
  };

  if (orders.length === 0) {
    return (
      <Box>
        <ListTitleBox
          title={
            <Typography variant="h4">
              {t('customer-orders.list.title')}
            </Typography>
          }
          onBackClick={handleBackClick}
          paddingBottom={0.5}
        />
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
          }}
        >
          <Typography variant="h5" component="div" gutterBottom>
            {t('customer-orders.list.no-orders')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <ListTitleBox
        title={
          <Typography variant="h4">
            {t('customer-orders.list.title')}
          </Typography>
        }
        onBackClick={handleBackClick}
        paddingBottom={0.5}
      />

      <Grid container spacing={2}>
        {[...orders]
          .sort(
            (a, b) =>
              new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
          )
          .map((order) => (
            <Grid item xs={12} key={order.id}>
              <Card
                sx={{
                  p: 3,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: (theme) => theme.shadows[4],
                    transition: 'box-shadow 0.2s ease-in-out',
                  },
                }}
                onClick={() => navigate(`/customer/orders/${order.id}`)}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        gap: { xs: 1, sm: 2 },
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        }}
                      >
                        #{order.tokenNumber}
                      </Typography>
                      {order.restaurantName && (
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                          }}
                        >
                          {order.restaurantName}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        icon={<AccessTimeIcon />}
                        label={new Date(order.orderDate).toLocaleString()}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        {...getOrderTypeChipProps(order.orderType)}
                        label={t(getOrderTypeChipProps(order.orderType).label)}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'flex-start', sm: 'flex-end' },
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <OrderStatusLabel orderStatus={order.orderStatus} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      }}
                    >
                      {formatCurrency(order.total)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}
